import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import {
  SearchIcon,
  EditIcon,
  TrashIcon,
  DoubleCheckIcon,
  BottomArrowIcon,
} from "../../components/Icons";
import Layout from "../../components/layout/Layout";
import {
  useModal,
  AddCustomProcedureModalContent,
  CustomProcedureFormData,
  EditCustomProcedureModalContent,
  EditCustomProcedureFormData,
  DeleteCustomProcedureModalContent,
  ImportFeesModalContent,
} from "../../components/Modal";
import { Typography } from "@mui/material";
import {
  PageContainer,
  PageHeaderContainer,
  TitleRow,
  HeaderActions,
  SearchContainer,
  ImportButton,
  UpdatesSavedBadge,
  ContentContainer,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  AccordionTitle,
  AccordionCodeRange,
  SearchResultsCount,
  AddCustomProcedureButton,
  ProcedureRow,
  ProcedureCode,
  ProcedureName,
  ProcedureDescription,
  InputContainer,
  InputLabel,
  NumberInput,
  ActionButtons,
  ActionButton,
  NoResultsMessage,
  CustomProcedureHeader,
} from "./ManageOrganizationPage.styled";
import {
  ProcedureCategory,
  CategoryProcedure,
  CustomProcedure,
  organizationService,
  ImportFeesResult,
} from "../../services/organizationService";
import { colors } from "../../theme/colors";
import { CircularProgress, Box } from "@mui/material";

const ADD_CUSTOM_PROCEDURE_MODAL_ID = "add-custom-procedure-modal";
const EDIT_CUSTOM_PROCEDURE_MODAL_ID = "edit-custom-procedure-modal";
const DELETE_CUSTOM_PROCEDURE_MODAL_ID = "delete-custom-procedure-modal";
const IMPORT_FEES_MODAL_ID = "import-fees-modal";

type NotificationType = "saved" | "deleted" | "error";

interface NotificationState {
  show: boolean;
  type: NotificationType;
  message?: string;
}

const ManageOrganizationPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<ProcedureCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({
    show: false,
    type: "saved",
  });
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadDone = useRef(false);

  // Load all procedures from API (grouped by category)
  const loadProcedures = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);

    try {
      const data = await organizationService.getProcedures();
      setCategories(data);

      // Expand first category by default if none expanded
      if (data.length > 0) {
        setExpandedCategories((prev) =>
          prev.length === 0 ? [data[0].category] : prev,
        );
      }
    } catch (error) {
      console.error("Error loading procedures:", error);
      setCategories([]);
    }

    if (showLoader) setLoading(false);
  }, []);

  // Initial load - runs only once on mount
  useEffect(() => {
    if (isInitialLoadDone.current) return;
    isInitialLoadDone.current = true;
    loadProcedures(true);
  }, [loadProcedures]);

  // Handle accordion expand/collapse
  const handleAccordionChange = useCallback(
    (categoryName: string) =>
      (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedCategories((prev) =>
          isExpanded
            ? [...prev, categoryName]
            : prev.filter((name) => name !== categoryName),
        );
      },
    [],
  );

  // Filter categories and procedures by search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;

    const query = searchQuery.toLowerCase().trim();
    return categories.map((category) => ({
      ...category,
      procedures: category.procedures.filter(
        (p) =>
          (p.codes &&
            p.codes.some(
              (code) => code && code.toLowerCase().includes(query),
            )) ||
          (p.name && p.name.toLowerCase().includes(query)),
      ),
    }));
  }, [categories, searchQuery]);

  // Get search results count per category
  const getSearchResultsCount = useCallback(
    (categoryName: string) => {
      if (!searchQuery.trim()) return 0;
      const category = filteredCategories.find(
        (c) => c.category === categoryName,
      );
      return category?.procedures.length || 0;
    },
    [filteredCategories, searchQuery],
  );

  // Show notification indicator
  const showNotification = useCallback(
    (type: NotificationType, message?: string) => {
      setNotification({ show: true, type, message });
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(() => {
        setNotification((prev) => ({ ...prev, show: false }));
      }, 3000);
    },
    [],
  );

  // Show updates saved indicator
  const showSavedIndicator = useCallback(() => {
    showNotification("saved");
  }, [showNotification]);

  // Show error indicator
  const showErrorIndicator = useCallback(
    (message?: string) => {
      showNotification(
        "error",
        message ||
          t("organizationManagement.errorSaving", "Failed to save changes"),
      );
    },
    [showNotification, t],
  );

  // Handle procedure update (fee or duration)
  const handleProcedureUpdate = useCallback(
    async (
      procedureId: number,
      cdtCodeId: number,
      field: "fee" | "durationMinutes",
      value: number,
      isCustom: boolean,
    ) => {
      try {
        if (isCustom) {
          // Find the procedure to get all its data for custom procedures
          let procedure: CategoryProcedure | undefined;
          for (const category of categories) {
            procedure = category.procedures.find(
              (p) => p.customProcedureId === procedureId,
            );
            if (procedure) break;
          }

          if (!procedure) {
            console.error("Procedure not found:", procedureId);
            return;
          }

          // Custom procedures use PUT /organization/procedures/{id}
          await organizationService.updateCustomProcedure(procedureId, {
            name: procedure.name,
            description: procedure.description,
            fee: field === "fee" ? value : procedure.fee,
            durationMinutes:
              field === "durationMinutes" ? value : procedure.durationMinutes,
          });
        } else {
          // Standard procedures use PUT /cdt-codes/{id}/duration or /fee
          if (field === "durationMinutes") {
            await organizationService.updateCdtCodeDuration(cdtCodeId, value);
          } else {
            await organizationService.updateCdtCodeFee(cdtCodeId, value);
          }
        }

        // Update local state
        setCategories((prev) =>
          prev.map((category) => ({
            ...category,
            procedures: category.procedures.map((p) =>
              p.customProcedureId === procedureId
                ? { ...p, [field]: value }
                : p,
            ),
          })),
        );

        showSavedIndicator();
      } catch (error) {
        console.error("Error updating procedure:", error);
        showErrorIndicator();
      }
    },
    [categories, showSavedIndicator, showErrorIndicator],
  );

  // Handle actual import of fees - calls real API
  const handleImportFile = useCallback(
    async (file: File): Promise<ImportFeesResult> => {
      const result = await organizationService.importFees(file);

      // If import was successful (at least some rows succeeded), refresh all data
      if (result.successCount > 0) {
        await loadProcedures();
        showSavedIndicator();
      }

      return result;
    },
    [loadProcedures, showSavedIndicator],
  );

  // Handle import fees button click
  const handleImportFees = useCallback(() => {
    openModal({
      id: IMPORT_FEES_MODAL_ID,
      content: (
        <ImportFeesModalContent
          onImport={handleImportFile}
          onClose={() => closeModal(IMPORT_FEES_MODAL_ID)}
        />
      ),
      showCloseButton: false,
      maxWidth: false,
      width: 576,
      containerPadding: "0",
    });
  }, [openModal, closeModal, handleImportFile]);

  // Handle adding a new custom procedure
  const handleAddProcedure = useCallback(
    async (data: CustomProcedureFormData) => {
      try {
        await organizationService.createCustomProcedure({
          codeIds: data.cdtCodeIds,
          name: data.name,
          description: data.description,
          fee: data.fee,
          durationMinutes: data.durationMinutes,
        });

        // Reload procedures to get updated list
        await loadProcedures();
        showSavedIndicator();
      } catch (error) {
        console.error("Error creating custom procedure:", error);
        throw error;
      }
    },
    [loadProcedures, showSavedIndicator],
  );

  // Handle add custom procedure button click
  const handleAddCustomProcedure = useCallback(() => {
    openModal({
      id: ADD_CUSTOM_PROCEDURE_MODAL_ID,
      content: (
        <AddCustomProcedureModalContent
          onAdd={handleAddProcedure}
          onClose={() => closeModal(ADD_CUSTOM_PROCEDURE_MODAL_ID)}
        />
      ),
      showCloseButton: false,
      maxWidth: false,
      width: 720,
      containerPadding: "0",
    });
  }, [openModal, closeModal, handleAddProcedure]);

  // Handle saving edited custom procedure
  const handleSaveEditedProcedure = useCallback(
    async (procedureId: number, data: EditCustomProcedureFormData) => {
      try {
        await organizationService.updateCustomProcedure(procedureId, {
          codeIds: data.cdtCodeIds,
          name: data.name,
          description: data.description,
          fee: data.fee,
          durationMinutes: data.durationMinutes,
        });

        // Reload procedures to get updated list
        await loadProcedures();
        showSavedIndicator();
      } catch (error) {
        console.error("Error updating custom procedure:", error);
        throw error;
      }
    },
    [loadProcedures, showSavedIndicator],
  );

  // Handle edit custom procedure button click
  const handleEditProcedure = useCallback(
    (procedure: CategoryProcedure) => {
      const customProcedure: CustomProcedure = {
        id: procedure.customProcedureId,
        cdtCodeId: procedure.cdtCodeId,
        codes: procedure.codes,
        name: procedure.name,
        description: procedure.description,
        fee: procedure.fee,
        durationMinutes: procedure.durationMinutes,
      };

      openModal({
        id: EDIT_CUSTOM_PROCEDURE_MODAL_ID,
        content: (
          <EditCustomProcedureModalContent
            procedure={customProcedure}
            onSave={(data) =>
              handleSaveEditedProcedure(procedure.customProcedureId, data)
            }
            onClose={() => closeModal(EDIT_CUSTOM_PROCEDURE_MODAL_ID)}
          />
        ),
        showCloseButton: false,
        maxWidth: false,
        width: 720,
        containerPadding: "0",
      });
    },
    [openModal, closeModal, handleSaveEditedProcedure],
  );

  // Handle actual deletion of procedure
  const handleConfirmDelete = useCallback(
    async (procedureId: number, procedureName: string) => {
      try {
        await organizationService.deleteCustomProcedure(procedureId);

        // Reload procedures to get updated list
        await loadProcedures();
        showNotification("deleted", procedureName);
      } catch (error) {
        console.error("Error deleting custom procedure:", error);
        throw error;
      }
    },
    [loadProcedures, showNotification],
  );

  // Handle delete custom procedure button click
  const handleDeleteProcedure = useCallback(
    (procedureId: number, procedureName: string) => {
      openModal({
        id: DELETE_CUSTOM_PROCEDURE_MODAL_ID,
        content: (
          <DeleteCustomProcedureModalContent
            procedureName={procedureName}
            onDelete={() => handleConfirmDelete(procedureId, procedureName)}
            onClose={() => closeModal(DELETE_CUSTOM_PROCEDURE_MODAL_ID)}
          />
        ),
        showCloseButton: false,
        maxWidth: false,
        width: 576,
        containerPadding: "0",
      });
    },
    [openModal, closeModal, handleConfirmDelete],
  );

  // Check if search has results
  const hasSearchResults = useMemo(() => {
    if (!searchQuery) return true;
    return filteredCategories.some((c) => c.procedures.length > 0);
  }, [filteredCategories, searchQuery]);

  // Check if category is custom
  const isCustomCategory = useCallback((categoryName: string) => {
    return categoryName.toLowerCase().includes("custom");
  }, []);

  // Render procedure row
  const renderProcedureRow = useCallback(
    (procedure: CategoryProcedure, isCustom: boolean) => {
      const codesDisplay = (procedure.codes || []).filter(Boolean).join(", ");

      return (
        <ProcedureRow
          key={procedure.customProcedureId}
          className={isCustom ? "" : "standard-procedure"}
        >
          <ProcedureCode>{codesDisplay}</ProcedureCode>
          <ProcedureName>{procedure.name}</ProcedureName>
          <ProcedureDescription>{procedure.description}</ProcedureDescription>

          <InputContainer>
            <InputLabel>
              {t("organizationManagement.durationLabel", "Duration, min")}
            </InputLabel>
            <NumberInput
              type="number"
              min="0"
              defaultValue={procedure.durationMinutes}
              onBlur={async (e) => {
                const newValue = parseInt(e.target.value) || 0;
                if (newValue !== procedure.durationMinutes) {
                  await handleProcedureUpdate(
                    procedure.customProcedureId,
                    procedure.cdtCodeId,
                    "durationMinutes",
                    newValue,
                    isCustom,
                  );
                }
              }}
            />
          </InputContainer>

          <InputContainer>
            <InputLabel>
              {t("organizationManagement.feesLabel", "Fees, $")}
            </InputLabel>
            <NumberInput
              type="number"
              min="0"
              step="0.01"
              defaultValue={procedure.fee}
              onBlur={async (e) => {
                const newValue = parseFloat(e.target.value) || 0;
                if (newValue !== procedure.fee) {
                  await handleProcedureUpdate(
                    procedure.customProcedureId,
                    procedure.cdtCodeId,
                    "fee",
                    newValue,
                    isCustom,
                  );
                }
              }}
            />
          </InputContainer>

          {isCustom && (
            <ActionButtons>
              <ActionButton onClick={() => handleEditProcedure(procedure)}>
                <EditIcon fill={colors.primary.main} />
              </ActionButton>
              <ActionButton
                onClick={() =>
                  handleDeleteProcedure(
                    procedure.customProcedureId,
                    procedure.name,
                  )
                }
              >
                <TrashIcon fill={colors.status.error} />
              </ActionButton>
            </ActionButtons>
          )}
        </ProcedureRow>
      );
    },
    [handleProcedureUpdate, handleEditProcedure, handleDeleteProcedure, t],
  );

  // Render category accordion
  const renderCategoryAccordion = useCallback(
    (category: ProcedureCategory) => {
      const isExpanded = expandedCategories.includes(category.category);
      const isCustom = isCustomCategory(category.category);
      const searchResultsCount = getSearchResultsCount(category.category);

      // Get code range for display (from original categories, not filtered)
      const originalCategory = categories.find(
        (c) => c.category === category.category,
      );
      const codeRange =
        originalCategory && originalCategory.procedures.length > 0
          ? (() => {
              const allCodes = originalCategory.procedures
                .flatMap((p) => p.codes || [])
                .filter(Boolean)
                .sort();
              if (allCodes.length === 0) return null;
              if (allCodes.length === 1) return allCodes[0];
              return `${allCodes[0]} - ${allCodes[allCodes.length - 1]}`;
            })()
          : null;

      return (
        <StyledAccordion
          key={category.category}
          expanded={isExpanded}
          onChange={handleAccordionChange(category.category)}
        >
          <StyledAccordionSummary
            expandIcon={<BottomArrowIcon width="20" height="20" />}
          >
            <CustomProcedureHeader>
              <AccordionTitle>{category.category}</AccordionTitle>
              {searchQuery.trim() && searchResultsCount > 0 && (
                <SearchResultsCount>
                  Search Results: {searchResultsCount}
                </SearchResultsCount>
              )}
            </CustomProcedureHeader>
            {isCustom && (
              <AddCustomProcedureButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddCustomProcedure();
                }}
              >
                {t(
                  "organizationManagement.addCustomProcedure",
                  "Add Custom Procedure",
                )}
              </AddCustomProcedureButton>
            )}
            {codeRange && !isCustom && (
              <AccordionCodeRange>{codeRange}</AccordionCodeRange>
            )}
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {category.procedures.length > 0 ? (
              category.procedures.map((procedure) =>
                renderProcedureRow(procedure, isCustom),
              )
            ) : (
              <NoResultsMessage>
                {isCustom
                  ? t(
                      "organizationManagement.noCustomProcedures",
                      "No custom procedures added yet",
                    )
                  : t(
                      "organizationManagement.noProcedures",
                      "No procedures found",
                    )}
              </NoResultsMessage>
            )}
          </StyledAccordionDetails>
        </StyledAccordion>
      );
    },
    [
      categories,
      expandedCategories,
      getSearchResultsCount,
      handleAccordionChange,
      handleAddCustomProcedure,
      isCustomCategory,
      renderProcedureRow,
      searchQuery,
      t,
    ],
  );

  // Get notification message
  const getNotificationMessage = () => {
    if (notification.type === "deleted") {
      return t(
        "organizationManagement.procedureDeleted",
        "{{name}} has been successfully removed",
        { name: notification.message },
      );
    }
    if (notification.type === "error") {
      return (
        notification.message ||
        t("organizationManagement.errorSaving", "Failed to save changes")
      );
    }
    return t("organizationManagement.updatesSaved", "Updates Saved");
  };

  return (
    <Layout>
      <PageContainer>
        <PageHeaderContainer>
          <TitleRow>
            <Typography variant="h1">
              {t("organizationManagement.title", "Organization Management")}
            </Typography>
            <HeaderActions>
              {notification.show && (
                <UpdatesSavedBadge $isError={notification.type === "error"}>
                  <DoubleCheckIcon
                    width="16px"
                    height="16px"
                    fill={notification.type === "error" ? "#dc2626" : "#16a34a"}
                  />
                  {getNotificationMessage()}
                </UpdatesSavedBadge>
              )}
              <SearchContainer>
                <SearchIcon
                  width="18px"
                  height="18px"
                  fill={colors.text.secondary}
                />
                <input
                  placeholder={t(
                    "organizationManagement.searchPlaceholder",
                    "Search CDT code",
                  )}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchContainer>
              <ImportButton variant="contained" onClick={handleImportFees}>
                {t("organizationManagement.importFees", "Import Fees")}
              </ImportButton>
            </HeaderActions>
          </TitleRow>
        </PageHeaderContainer>

        <ContentContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : !hasSearchResults ? (
            <NoResultsMessage>
              {t("organizationManagement.noResults", "No procedure found")}
            </NoResultsMessage>
          ) : (
            filteredCategories.map((category) =>
              renderCategoryAccordion(category),
            )
          )}
        </ContentContainer>
      </PageContainer>
    </Layout>
  );
};

export default ManageOrganizationPage;
